// Calls & Conversations: call outcomes, durations, date ranges and the call timeline (shared by the list, the details
// panel, the full call page, the dashboard and analytics).
import { prettyNumber } from '../CallDialog';

export const CALL_STATUS = {
  initiating: ['Calling you…', 'pending'], ringing: ['Ringing', 'pending'], in_progress: ['In progress', 'pending'],
  completed: ['Answered', 'green'], no_answer: ['No answer', 'red'], busy: ['Busy', 'red'], failed: ['Failed', 'red'],
  missed: ['Missed', 'red'], voicemail: ['Voicemail', 'blue'],
};
export const LIVE_STATUSES = ['initiating', 'ringing', 'in_progress'];

export const formatDuration = (s) => (s == null ? '—' : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`);

// Date ranges are the user's local days, sent to the server as exact times so the timezone is handled there.
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const RANGES = {
  today: ['Today', () => [startOfDay(new Date()), addDays(new Date(), 1)]],
  yesterday: ['Yesterday', () => [addDays(new Date(), -1), startOfDay(new Date())]],
  7: ['Last 7 days', () => [addDays(new Date(), -6), addDays(new Date(), 1)]],
  30: ['Last 30 days', () => [addDays(new Date(), -29), addDays(new Date(), 1)]],
  month: ['This month', () => [new Date(new Date().getFullYear(), new Date().getMonth(), 1), addDays(new Date(), 1)]],
  custom: ['Custom dates', null],
};
const fromInput = (v) => { const [y, m, d] = v.split('-').map(Number); return new Date(y, m - 1, d); };

export function rangeParams(range, from, to) {
  if (!range) return {};
  let start;
  let end;
  if (range === 'custom') {
    start = from ? fromInput(from) : null;
    end = to ? addDays(fromInput(to), 1) : null;
  } else [start, end] = RANGES[range][1]();
  return { ...(start && { start: start.toISOString() }), ...(end && { end: end.toISOString() }) };
}

export const callDay = (iso) => {
  const d = new Date(`${iso}Z`);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const when = (iso) => (iso ? new Date(`${iso}Z`).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null);

// Plivo hang-up causes are codes like NORMAL_CLEARING; show them as words.
const CAUSES = { NORMAL_CLEARING: 'Normal hang-up', USER_BUSY: 'Line busy', NO_ANSWER: 'No answer', CALL_REJECTED: 'Call rejected', ORIGINATOR_CANCEL: 'Caller hung up' };
export const cause = (c) => (c ? CAUSES[c] || c.replace(/_/g, ' ').toLowerCase().replace(/^./, (x) => x.toUpperCase()) : null);

const NOT_ANSWERED = { no_answer: 'Nobody answered', busy: 'The line was busy', missed: 'Nobody answered', voicemail: 'Nobody answered, so the caller was offered voicemail', failed: 'The call could not be connected' };

/** [text, time, colour] for each thing that happened during the call. */
export function timeline(call) {
  const steps = [[
    call.direction === 'inbound' ? `Call came in to ${prettyNumber(call.company_number)}` : `${call.user_name || 'A team member'} started the call from ${prettyNumber(call.company_number)}`,
    when(call.started_at), '#2563eb',
  ]];
  if (call.answered_at) steps.push(['Answered', when(call.answered_at), '#10b981']);
  else if (NOT_ANSWERED[call.status]) steps.push([NOT_ANSWERED[call.status], null, call.status === 'voicemail' ? '#2563eb' : '#f43f5e']);
  if (call.status === 'voicemail' && call.recording_id) steps.push(['Voicemail left', null, '#9333ea']);
  if (call.ended_at) steps.push([`Call ended${cause(call.hangup_cause) ? ` · ${cause(call.hangup_cause)}` : ''}`, when(call.ended_at), '#64748b']);
  else if (LIVE_STATUSES.includes(call.status)) steps.push([`${CALL_STATUS[call.status][0]} (updates automatically)`, null, '#f59e0b']);
  return steps;
}

export const EMPTY_FILTERS = { direction: '', status: '', range: '', from: '', to: '' };
